import { Component, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Context, RuleInterface } from '@vendasta/automata';
import { TriggerService } from '../../../../trigger.service';
import { AutomataContextService } from '@galaxy/automata/shared';
import { UsersSelectorService } from '../../../../../data-services/users-selector-service';
import { WORKFLOW_TRIGGERS } from '../../../../component-loader/trigger-definitions';
import {
  adminsAndSalespeople,
  calculateDropdownValueFromJsonData,
  onlyAdmins,
  Options,
  specificUsers,
} from '../manually-trigger-common';
import { NoRules } from '../../common';
import { TriggerBaseRules } from '../../../common/decorators/trigger-rules.decorator';
import { AutomationReplacementChipsComponent } from '../../../../../common/components';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { CrmCustomObjectTypeSelectorService } from '../../../../../data-services/crm-custom-object-type-selector.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'automata-manually-trigger-custom-object-automation',
  imports: [
    CommonModule,
    AutomationReplacementChipsComponent,
    GalaxyFormFieldModule,
    MatOption,
    MatSelect,
    TranslateModule,
    ReactiveFormsModule,
  ],
  templateUrl: './manually-trigger-custom-object-panel.component.html',
  styleUrl: './manually-trigger-custom-object-panel.component.scss',
})
export class ManuallyTriggerCustomObjectPanelComponent extends CommonTriggerPanelComponent implements OnInit {
  automationContext = this.automationContextService.currentContext;
  context = Context;

  crmCustomObjectTypeSelectorService = inject(CrmCustomObjectTypeSelectorService);
  customObjectTypeFormControl = new FormControl<string | null>(null);

  customObjectTypeFormValue = toSignal(this.customObjectTypeFormControl.valueChanges);
  hasObjectTypeSelected = computed(() => !!this.customObjectTypeFormValue());

  salesRunnable = false;
  usersFormControl: FormControl<string[]> = new FormControl<string[]>([]);
  automationId: string = this.triggerService.automationId;
  userSelectorService = inject(UsersSelectorService);
  options = Options;
  selectedOption: string = onlyAdmins;
  readonly specificUsers = specificUsers;
  nameKey = '';

  constructor(
    protected readonly triggerService: TriggerService,
    private readonly automationContextService: AutomataContextService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const triggerDefinitions: { [taskKey: string]: any } = WORKFLOW_TRIGGERS;
    this.nameKey = triggerDefinitions?.[this.taskDefinitionId]?.name;
    const jsonData = this.step ? JSON.parse(this.step?.data ?? '{}') : {};
    this.salesRunnable = !!jsonData.sales_runnable;
    this.customObjectTypeFormControl.setValue(jsonData.user_provided_custom_object_type_id ?? null);
    this.usersFormControl.setValue(jsonData?.only_allowed_users?.length > 0 ? jsonData?.only_allowed_users : []);
    this.selectedOption = calculateDropdownValueFromJsonData(this.salesRunnable, this.usersFormControl.value);
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.customObjectTypeFormControl];
  }

  isFormValid(): boolean {
    return this.customObjectTypeFormControl.valid;
  }

  @TriggerBaseRules
  getBaseRules(): RuleInterface[] {
    return NoRules();
  }

  fillRuleValues(rules: RuleInterface[]): RuleInterface[] {
    return rules;
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step?.data ?? '{}') : {};
    jsonData.user_provided_custom_object_type_id = this.customObjectTypeFormControl.value;
    jsonData.sales_runnable = this.selectedOption === adminsAndSalespeople;
    jsonData.only_allowed_users = this.selectedOption === specificUsers ? this.usersFormControl.value : [];
    return jsonData;
  }

  finishedLoadingSelector(): void {
    this.savingEnabled$.next(true);
  }
}
